import { useCallback, useEffect, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionGroup, AccordionSummary,
    Alert, Box, Button, Card, Chip, CircularProgress, Divider, Input,
    Modal, ModalDialog, Sheet, Stack, Tooltip, Typography,
} from '@mui/joy';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';

import { useAdvertiser } from '../../AdvertiserContext';
import {
    APIAdsCreativeDelete, APIAdsGroupCreate, APIAdsGroupDelete,
    APIAdsGroupRotation, APIAdsGroupUpdate, APIAdsTargetingUpdate, adsErrorText,
} from '../../api_ads';
import AdCreativeEditor from './AdCreativeEditor';
import AdCreativePreview from './AdCreativePreview';
import AdStatusChip from './AdStatusChip';
import AdTargetingEditor from './AdTargetingEditor';
import { FORMAT_LABELS, ROTATION_LABELS } from './adsConstants';


function GroupNameModal({ open, onClose, initial, onSubmit, saving }) {
    const [name, setName] = useState('');
    useEffect(() => { if (open) setName(initial || ''); }, [open, initial]);

    return (
        <Modal open={open} onClose={saving ? undefined : onClose}>
            <ModalDialog sx={{ minWidth: 340 }}>
                <Typography level="title-lg">
                    {initial ? 'Переименовать группу' : 'Новая группа объявлений'}
                </Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 1.5 }}>
                    Группа — это одна аудитория с одной ставкой. Разные аудитории
                    удобнее развести по разным группам, чтобы видеть их отдельно в отчётах.
                </Typography>
                <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Например: Женщины 25–34, Москва"
                    autoFocus
                />
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button variant="outlined" color="neutral" fullWidth onClick={onClose} disabled={saving}>
                        Отмена
                    </Button>
                    <Button fullWidth loading={saving} onClick={() => onSubmit(name.trim())}>
                        {initial ? 'Сохранить' : 'Создать'}
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


function TargetingModal({ open, onClose, group, onSaved }) {
    const { advertiserToken: token } = useAdvertiser();
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) { setDraft(group?.targeting || {}); setError(null); }
    }, [open, group]);

    const save = async () => {
        try {
            setSaving(true);
            setError(null);
            await APIAdsTargetingUpdate(token, group.id, draft);
            onSaved?.();
            onClose?.();
        } catch (e) {
            setError(adsErrorText(e, 'Не удалось сохранить таргетинг'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={saving ? undefined : onClose}>
            <ModalDialog sx={{ width: { xs: '96vw', md: 720 }, maxHeight: '92vh', overflowY: 'auto' }}>
                <Typography level="title-lg">Таргетинг · {group?.name}</Typography>
                {error && <Alert color="danger" sx={{ mt: 1.5 }}>{error}</Alert>}
                <Box sx={{ mt: 2 }}>
                    <AdTargetingEditor value={draft} onChange={setDraft} />
                </Box>
                <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                    <Button variant="outlined" color="neutral" fullWidth onClick={onClose} disabled={saving}>
                        Отмена
                    </Button>
                    <Button fullWidth onClick={save} loading={saving}>Сохранить таргетинг</Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


/**
 * Группы кампании со всем содержимым: таргетинг и объявления.
 *
 * `editable` повторяет серверное правило: структуру кампании можно менять
 * только в статусах draft, rejected и paused. Кнопки не прячем, а
 * блокируем с подсказкой — иначе непонятно, куда они делись.
 */
export default function AdGroupsManager({ campaign, allowedFormats, editable, onChanged }) {
    const { advertiserToken: token } = useAdvertiser();
    const groups = campaign?.groups || [];

    const [groupModal, setGroupModal] = useState({ open: false, group: null });
    const [targetingModal, setTargetingModal] = useState({ open: false, group: null });
    const [creativeModal, setCreativeModal] = useState({ open: false, groupId: null, creative: null });
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);

    const disabledHint = editable
        ? ''
        : 'Остановите кампанию, чтобы менять её структуру';

    const createGroup = async (name) => {
        if (!name) return;
        try {
            setBusy('group');
            setError(null);
            await APIAdsGroupCreate(token, campaign.id, { name });
            setGroupModal({ open: false, group: null });
            onChanged?.();
        } catch (e) {
            setError(adsErrorText(e));
        } finally {
            setBusy(null);
        }
    };

    const renameGroup = async (name) => {
        if (!name) return;
        try {
            setBusy('group');
            setError(null);
            await APIAdsGroupUpdate(token, groupModal.group.id, { name });
            setGroupModal({ open: false, group: null });
            onChanged?.();
        } catch (e) {
            setError(adsErrorText(e));
        } finally {
            setBusy(null);
        }
    };

    const deleteGroup = async (group) => {
        try {
            setBusy(`del-group-${group.id}`);
            setError(null);
            await APIAdsGroupDelete(token, group.id);
            onChanged?.();
        } catch (e) {
            setError(adsErrorText(e));
        } finally {
            setBusy(null);
        }
    };

    const changeRotation = async (group, mode) => {
        try {
            setBusy(`rotation-${group.id}`);
            setError(null);
            await APIAdsGroupRotation(token, group.id, mode);
            onChanged?.();
        } catch (e) {
            setError(adsErrorText(e));
        } finally {
            setBusy(null);
        }
    };

    const deleteCreative = async (creative) => {
        try {
            setBusy(`del-creative-${creative.id}`);
            setError(null);
            await APIAdsCreativeDelete(token, creative.id);
            onChanged?.();
        } catch (e) {
            setError(adsErrorText(e));
        } finally {
            setBusy(null);
        }
    };

    return (
        <Box>
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography level="title-sm">
                    Группы объявлений ({groups.length})
                </Typography>
                <Tooltip title={disabledHint} arrow disableHoverListener={editable}>
                    <span>
                        <Button
                            size="sm"
                            startDecorator={<AddRoundedIcon />}
                            disabled={!editable}
                            onClick={() => setGroupModal({ open: true, group: null })}
                        >
                            Добавить группу
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            {groups.length === 0 ? (
                <Alert color="warning">
                    В кампании нет ни одной группы. Добавьте группу, задайте таргетинг
                    и загрузите хотя бы одно объявление — без этого кампанию нельзя
                    отправить на модерацию.
                </Alert>
            ) : (
                <AccordionGroup variant="outlined" sx={{ borderRadius: 'md' }}>
                    {groups.map((group) => {
                        const creatives = group.creatives || [];
                        return (
                            <Accordion key={group.id} defaultExpanded={groups.length === 1}>
                                <AccordionSummary>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{ width: '100%' }}
                                        flexWrap="wrap"
                                    >
                                        <Typography level="title-sm">{group.name}</Typography>
                                        <Chip size="sm" variant="soft">
                                            Объявлений: {creatives.length}
                                        </Chip>
                                        {!group.is_enabled && (
                                            <Chip size="sm" variant="soft" color="neutral">
                                                Выключена
                                            </Chip>
                                        )}
                                    </Stack>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                                        <Button
                                            size="sm"
                                            variant="soft"
                                            startDecorator={<TuneRoundedIcon />}
                                            disabled={!editable}
                                            onClick={() => setTargetingModal({ open: true, group })}
                                        >
                                            Таргетинг
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="soft"
                                            startDecorator={<AddRoundedIcon />}
                                            disabled={!editable || !allowedFormats.length}
                                            onClick={() => setCreativeModal({
                                                open: true, groupId: group.id, creative: null,
                                            })}
                                        >
                                            Добавить объявление
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="plain"
                                            color="neutral"
                                            startDecorator={<EditRoundedIcon />}
                                            disabled={!editable}
                                            onClick={() => setGroupModal({ open: true, group })}
                                        >
                                            Переименовать
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="plain"
                                            color="danger"
                                            startDecorator={<DeleteOutlineRoundedIcon />}
                                            disabled={!editable}
                                            loading={busy === `del-group-${group.id}`}
                                            onClick={() => deleteGroup(group)}
                                        >
                                            Удалить группу
                                        </Button>
                                    </Stack>

                                    {/* Ротация объявлений внутри группы */}
                                    {creatives.length > 1 && (
                                        <Sheet
                                            variant="soft"
                                            sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}
                                        >
                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={1.5}
                                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                                justifyContent="space-between"
                                            >
                                                <Box>
                                                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                                        Показ объявлений
                                                    </Typography>
                                                    <Typography level="body-xs" color="neutral">
                                                        {group.rotation_mode === 'even'
                                                            ? 'Все объявления получают одинаковую долю показов'
                                                            : 'Больше показов у объявлений с лучшей отдачей, '
                                                              + 'новым гарантирован шанс набрать статистику'}
                                                    </Typography>
                                                </Box>
                                                <Stack direction="row" spacing={0.8}>
                                                    {Object.entries(ROTATION_LABELS).map(([mode, label]) => (
                                                        <Chip
                                                            key={mode}
                                                            variant={group.rotation_mode === mode
                                                                ? 'solid' : 'outlined'}
                                                            color={group.rotation_mode === mode
                                                                ? 'primary' : 'neutral'}
                                                            disabled={busy === `rotation-${group.id}`}
                                                            onClick={() => changeRotation(group, mode)}
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            {label}
                                                        </Chip>
                                                    ))}
                                                </Stack>
                                            </Stack>
                                        </Sheet>
                                    )}

                                    <Divider sx={{ mb: 2 }} />

                                    {creatives.length === 0 ? (
                                        <Alert color="warning" size="sm">
                                            В группе нет объявлений — показов не будет
                                        </Alert>
                                    ) : (
                                        <Stack spacing={1.5}>
                                            {creatives.map((creative) => (
                                                <Card key={creative.id} variant="outlined" sx={{ p: 1.5 }}>
                                                    <Stack
                                                        direction={{ xs: 'column', sm: 'row' }}
                                                        spacing={2}
                                                    >
                                                        <Box sx={{ flexShrink: 0 }}>
                                                            <AdCreativePreview
                                                                creative={creative}
                                                                format={creative.format}
                                                            />
                                                        </Box>

                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Stack
                                                                direction="row"
                                                                spacing={0.8}
                                                                alignItems="center"
                                                                flexWrap="wrap"
                                                            >
                                                                <Typography level="title-sm">
                                                                    {creative.title}
                                                                </Typography>
                                                                <AdStatusChip
                                                                    status={creative.moderation_status}
                                                                    kind="creative"
                                                                />
                                                                <Chip size="sm" variant="outlined">
                                                                    {FORMAT_LABELS[creative.format]
                                                                        || creative.format}
                                                                </Chip>
                                                            </Stack>

                                                            {creative.moderation_status === 'rejected' && (
                                                                <Alert color="danger" size="sm" sx={{ mt: 1 }}>
                                                                    <Box>
                                                                        <Typography
                                                                            level="body-sm"
                                                                            sx={{ fontWeight: 600 }}
                                                                        >
                                                                            {creative.rejection_reason_display
                                                                                || 'Отклонено'}
                                                                        </Typography>
                                                                        {creative.moderation_comment && (
                                                                            <Typography level="body-sm">
                                                                                {creative.moderation_comment}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Alert>
                                                            )}

                                                            <Typography
                                                                level="body-xs"
                                                                color="neutral"
                                                                sx={{ mt: 0.8 }}
                                                            >
                                                                {creative.brand_name} ·{' '}
                                                                показы {creative.impressions_total ?? 0} ·{' '}
                                                                клики {creative.clicks_total ?? 0}
                                                            </Typography>

                                                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                                <Button
                                                                    size="sm"
                                                                    variant="soft"
                                                                    startDecorator={<EditRoundedIcon />}
                                                                    onClick={() => setCreativeModal({
                                                                        open: true,
                                                                        groupId: group.id,
                                                                        creative,
                                                                    })}
                                                                >
                                                                    Изменить
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="plain"
                                                                    color="danger"
                                                                    disabled={!editable}
                                                                    loading={busy === `del-creative-${creative.id}`}
                                                                    onClick={() => deleteCreative(creative)}
                                                                >
                                                                    Удалить
                                                                </Button>
                                                            </Stack>
                                                        </Box>
                                                    </Stack>
                                                </Card>
                                            ))}
                                        </Stack>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </AccordionGroup>
            )}

            <GroupNameModal
                open={groupModal.open}
                initial={groupModal.group?.name}
                saving={busy === 'group'}
                onClose={() => setGroupModal({ open: false, group: null })}
                onSubmit={groupModal.group ? renameGroup : createGroup}
            />

            <TargetingModal
                open={targetingModal.open}
                group={targetingModal.group}
                onClose={() => setTargetingModal({ open: false, group: null })}
                onSaved={onChanged}
            />

            <AdCreativeEditor
                open={creativeModal.open}
                groupId={creativeModal.groupId}
                creative={creativeModal.creative}
                allowedFormats={allowedFormats}
                onClose={() => setCreativeModal({ open: false, groupId: null, creative: null })}
                onSaved={onChanged}
            />
        </Box>
    );
}